import type { MarketLevelResult } from "@shared/labs/candidate-assessment";
import { useInferMarketLevel } from "@/pages/labs/_shared/use-infer-market-level";
import { useMarkdownHtml } from "@/pages/labs/_shared/use-markdown-html";

export interface MarketLevelSectionProps {
  marketLevel?: MarketLevelResult;
  archivedEvaluationId?: string;
  onMarketLevelComputed?: (result: MarketLevelResult) => void;
}

export function MarketLevelSection({
  marketLevel,
  archivedEvaluationId,
  onMarketLevelComputed,
}: MarketLevelSectionProps) {
  const { inferMarketLevel, loading, error } = useInferMarketLevel();
  const reasoningHtml = useMarkdownHtml(marketLevel?.reasoning ?? "");

  async function handleCompute() {
    if (!archivedEvaluationId) return;
    const result = await inferMarketLevel(archivedEvaluationId);
    if (result) onMarketLevelComputed?.(result);
  }

  const confidencePct = marketLevel ? Math.round(marketLevel.confidence * 100) : 0;

  const summaryLabel = marketLevel ? marketLevel.cglDisplayName : "não computado";

  const summaryTitle = marketLevel
    ? `${marketLevel.cglDisplayName} · ${marketLevel.marketLevel}`
    : "Nível de mercado não computado";

  return (
    <details className="market-level-disclosure" data-testid="market-level-section">
      <summary className="market-level-summary" title={summaryTitle}>
        <span className="market-level-disclosure-label">Nível de mercado inferido</span>
        {marketLevel ? (
          <span className="market-level-cgl" data-testid="market-level-cgl">
            {summaryLabel}
          </span>
        ) : (
          <span
            className="market-level-cgl market-level-cgl--missing"
            data-testid="market-level-cgl"
          >
            {summaryLabel}
          </span>
        )}
        <span className="market-level-chev" aria-hidden>
          <svg viewBox="0 0 14 14" width="12" height="12" focusable="false" role="presentation">
            <title>chevron</title>
            <path
              d="M3 5.5 L7 9.5 L11 5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </summary>

      <div className="market-level-body">
        {marketLevel ? (
          <div className="market-level-content" data-testid="market-level-badge">
            <div className="market-level-confidence" data-testid="market-level-confidence">
              <span className="market-level-conf-label">Confiança</span>
              <div className="market-level-conf-bar-wrap">
                <div className="market-level-conf-bar">
                  <div
                    className="market-level-conf-fill"
                    style={{ width: `${confidencePct}%` }}
                    aria-hidden
                  />
                </div>
                <span className="market-level-conf-pct">{confidencePct}%</span>
              </div>
            </div>

            {/* Reasoning rendered as markdown so the LLM can use paragraph
                breaks (blank lines), short lists, bold and italic. Tailwind
                prose is too generous for this cramped hero context, so
                child-element typography lives in styles.css. Falls back to
                escaped pre via the hook's catch if marked rejects the
                input. */}
            <div
              className="market-level-reasoning"
              data-testid="market-level-reasoning"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: rendered output is from a Zod-validated LLM string passed through marked; same pattern as frameworks-section.tsx
              dangerouslySetInnerHTML={{ __html: reasoningHtml }}
            />

            {archivedEvaluationId && (
              <div className="market-level-recompute-row">
                <button
                  type="button"
                  className="market-level-recompute-btn"
                  onClick={handleCompute}
                  disabled={loading}
                  data-testid="market-level-recompute-btn"
                >
                  {loading ? "Recalculando…" : "Recalcular"}
                </button>
                {error && (
                  <span className="market-level-error" data-testid="market-level-error">
                    {error}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="market-level-empty" data-testid="market-level-empty">
            <p className="market-level-empty-msg">
              Inferência de nível de mercado ainda não foi computada.
            </p>
            {archivedEvaluationId && (
              <div className="market-level-empty-actions">
                <button
                  type="button"
                  className="market-level-compute-btn"
                  onClick={handleCompute}
                  disabled={loading}
                  data-testid="market-level-compute-btn"
                >
                  {loading ? "Computando…" : "Computar agora"}
                </button>
              </div>
            )}
            {error && (
              <p className="market-level-error" data-testid="market-level-error">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
